<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <!-- Header with tier badge and close button -->
      <div class="modal-header">
        <div class="tier-header">
          <EnhancedTierBadge 
            :tier="tier" 
            variant="medal" 
            size="large"
            :glow-effect="true"
          />
          <h2 class="tier-title">{{ tierDisplayName }} Tier</h2>
        </div>
        <button class="close-btn" @click="$emit('close')" aria-label="Close expanded view">×</button>
      </div>

      <!-- Album count info -->
      <div class="album-count-info">
        <span class="count-text">{{ albums.length }} album{{ albums.length !== 1 ? 's' : '' }} in this tier</span>
      </div>

      <!-- Albums list in 3-column layout -->
      <div class="albums-list" v-if="albums.length > 0">
        <div 
          v-for="album in albums" 
          :key="album.id" 
          class="album-row"
          @click="handleAlbumClick(album)"
        >
          <!-- Column 1: Album Cover -->
          <div class="album-cover-column">
            <img 
              :src="album.cover_url" 
              :alt="`${album.artist} - ${album.title} album cover`"
              class="album-cover-img"
            />
          </div>
          
          <!-- Column 2: Artist & Title -->
          <div class="album-info-column">
            <p class="album-artist">{{ album.artist }}</p>
            <p class="album-title">{{ album.title }}</p>
          </div>
          
          <!-- Column 3: Tier Badge -->
          <div class="album-tier-column">
            <EnhancedTierBadge 
              :tier="album.tier" 
              variant="compact" 
              size="small"
            />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <div class="empty-icon">📭</div>
        <h3>No albums in this tier</h3>
        <p>This tier doesn't contain any albums yet.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import EnhancedTierBadge from './EnhancedTierBadge.vue'

const props = defineProps({
  tier: {
    type: String,
    required: true
  },
  albums: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['close', 'albumClicked'])

const tierDisplayName = computed(() => {
  // Convert tier name to display format
  const tierMap = {
    's': 'S',
    'a': 'A', 
    'b': 'B',
    'c': 'C',
    'd': 'D',
    'e': 'E',
    'not-rated-yet': 'Not Rated Yet',
    'now-listening-to': 'Now Listening',
    'tiering': 'Not Rated Yet',
    'not': 'Not Rated Yet',
    'now': 'Now Listening',
    'question': 'Not Rated Yet'
  }
  
  return tierMap[props.tier.toLowerCase()] || props.tier.toUpperCase()
})

const handleAlbumClick = (album) => {
  emit('albumClicked', album)
}

const handleEscapeKey = (event) => {
  if (event.key === 'Escape') {
    emit('close')
  }
}

// Add event listener for ESC key
import { onMounted, onBeforeUnmount } from 'vue'

onMounted(() => {
  window.addEventListener('keydown', handleEscapeKey)
  // Prevent body scroll when modal is open
  document.body.style.overflow = 'hidden'
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscapeKey)
  // Restore body scroll when modal is closed
  document.body.style.overflow = ''
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  animation: fadeIn 0.3s ease;
  padding: 20px;
  box-sizing: border-box;
}

@keyframes fadeIn {
  from { 
    opacity: 0; 
  }
  to { 
    opacity: 1; 
  }
}

.modal-content {
  position: relative;
  background: rgba(30, 30, 30, 0.98);
  color: #f3f3f3;
  border-radius: 12px;
  max-height: 90vh;
  overflow: hidden;
  width: 100%;
  max-width: 900px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: simpleFadeIn 0.2s ease;
  display: flex;
  flex-direction: column;
  /* Performance optimizations */
  will-change: auto;
  transform: translate3d(0, 0, 0);
}

@keyframes simpleFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.tier-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.tier-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.close-btn {
  background: linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%);
  backdrop-filter: blur(16px);
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.close-btn:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%);
  border-color: rgba(255, 255, 255, 0.25);
  color: white;
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.close-btn:active {
  opacity: 0.9;
}

.album-count-info {
  padding: 16px 32px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.count-text {
  font-size: 14px;
  color: rgba(243, 243, 243, 0.8);
  font-weight: 500;
}

.albums-list {
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 40px 32px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  /* Smooth scrolling optimizations */
  -webkit-overflow-scrolling: touch;
  transform: translateZ(0);
  /* Prevent scroll bounce on iOS */
  overscroll-behavior: contain;
  /* CSS containment for performance */
  contain: layout style;
  content-visibility: auto;
}

.albums-list::-webkit-scrollbar {
  width: 8px;
}

.albums-list::-webkit-scrollbar-track {
  background: transparent;
}

.albums-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.albums-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.album-row {
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: 20px;
  align-items: center;
  padding: 16px 20px;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  /* Simplified performance optimizations */
  contain: layout style;
}

.album-row:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.album-row:last-child {
  margin-bottom: 0;
}

.album-cover-column {
  display: flex;
  align-items: center;
}

.album-cover-img {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.album-row:hover .album-cover-img {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.album-info-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0; /* Allow text to truncate */
}

.album-artist {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.album-title {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: rgba(243, 243, 243, 0.8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.album-tier-column {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
  flex: 1;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.6;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
}

.empty-state p {
  font-size: 16px;
  color: rgba(243, 243, 243, 0.7);
  margin: 0;
  line-height: 1.5;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .modal-content {
    border-radius: 0;
    max-height: 100vh;
    height: 100vh;
    width: 100vw;
    max-width: none;
  }

  .modal-header {
    padding: max(20px, env(safe-area-inset-top)) 20px 20px 20px; /* Respect iOS safe area */
  }

  .tier-title {
    font-size: 20px;
  }

  .close-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
    border-radius: 8px;
  }

  .album-count-info {
    padding: 12px 20px;
  }

  .albums-list {
    padding: 20px 20px max(60px, env(safe-area-inset-bottom)) 20px; /* Increased bottom padding for mobile + safe area */
    /* Enhanced mobile scrolling */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }

  .album-row {
    grid-template-columns: 60px 1fr auto;
    gap: 16px;
    padding: 12px 16px;
    margin-bottom: 8px;
  }

  .album-cover-img {
    width: 48px;
    height: 48px;
  }

  .album-artist {
    font-size: 15px;
  }

  .album-title {
    font-size: 13px;
  }

  .empty-state {
    padding: 48px 20px;
  }

  .empty-icon {
    font-size: 48px;
    margin-bottom: 20px;
  }

  .empty-state h3 {
    font-size: 20px;
  }

  .empty-state p {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .tier-header {
    gap: 12px;
  }

  .tier-title {
    font-size: 18px;
  }

  .album-row {
    grid-template-columns: 50px 1fr auto;
    gap: 12px;
    padding: 10px 12px;
  }

  .album-cover-img {
    width: 40px;
    height: 40px;
  }

  .album-artist {
    font-size: 14px;
  }

  .album-title {
    font-size: 12px;
  }
}

/* Simplified animations - remove staggered entrance animations */
.album-row:nth-child(1) { animation-delay: 0s; }
.album-row:nth-child(2) { animation-delay: 0s; }
.album-row:nth-child(3) { animation-delay: 0s; }
.album-row:nth-child(4) { animation-delay: 0s; }
.album-row:nth-child(5) { animation-delay: 0s; }

/* Accessibility focus states */
.close-btn:focus-visible,
.album-row:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}

.album-row:focus {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .modal-content,
  .album-row,
  .album-cover-img,
  .close-btn {
    animation: none;
    transition: none;
  }
  
  .album-row:hover {
    transform: none;
  }
  
  .album-row:hover .album-cover-img {
    transform: none;
  }
}
</style>